import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../components/common/Button';
import { User, Truck, Building, Save, Eye, EyeOff, Lock, ShieldCheck, CreditCard, Hash, Info } from 'lucide-react';
import axios from 'axios';
import './AuthModal.css';

const AuthModal = ({ show, onComplete, onClose, initialMode = 'signup' }) => {
    const [isLogin, setIsLogin] = useState(initialMode === 'login');
    const [formData, setFormData] = useState({
        driverName: '',
        vehicleUnit: '',
        carrierName: '',
        truckPlate: '',
        dotNumber: '',
        password: '',
        passwordConfirm: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
    const [isMatching, setIsMatching] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        validatePassword(formData.password);
        setIsMatching(formData.password === formData.passwordConfirm || formData.passwordConfirm === '');
    }, [formData.password, formData.passwordConfirm]);

    const validatePassword = (pass) => {
        if (!pass) {
            setPasswordStrength({ score: 0, label: '', color: '' });
            return;
        }

        let score = 0;
        if (pass.length >= 8) score++;
        if (/[A-Z]/.test(pass)) score++;
        if (/[0-9]/.test(pass)) score++;
        if (/[^A-Za-z0-9]/.test(pass)) score++;

        let label = 'Weak';
        let color = '#ff4d4d';

        if (score === 2) {
            label = 'Fair (Add numbers/caps)';
            color = '#ffa502';
        } else if (score === 3) {
            label = 'Good';
            color = '#2ecc71';
        } else if (score >= 4) {
            label = 'Strong';
            color = '#27ae60';
        }

        setPasswordStrength({ score, label, color });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const toHex = (buffer) => Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isLogin) {
            if (formData.password.length < 8) {
                alert('Password must be at least 8 characters.');
                return;
            }
            if (formData.password !== formData.passwordConfirm) {
                alert('Passwords do not match.');
                return;
            }
        }

        // Hash Password (SHA-256)
        const enc = new TextEncoder();
        const data = enc.encode(formData.password);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        const passwordHash = toHex(digest);

        setIsLoading(true);
        try {
            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const response = await axios.post(`${apiBase}/api/profile/`, {
                ...formData,
                passwordHash,
                action: isLogin ? 'login' : 'signup'
            });

            // Still save to localStorage for faster reloads/offline support, 
            // but the source of truth is now the DB
            localStorage.setItem('spotlogs_profile', JSON.stringify(response.data));
            onComplete(response.data);
        } catch (error) {
            console.error("Auth error:", error);
            
            let msg = "An unexpected error occurred.";
            if (!error.response) {
                // Connection error (e.g. hitting localhost from production)
                msg = "Cannot connect to server. Please check your internet or ensure the backend is live.";
            } else if (error.response.status === 409 || (error.response.data?.driverName && !isLogin)) {
                msg = "This driver name is already registered. Please try a different name.";
            } else {
                msg = error.response.data?.error || (isLogin ? "Invalid driver name or password." : "Error creating account. Please try again.");
            }
            
            alert(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="auth-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="auth-card"
                        initial={{ opacity: 0, y: 30, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Header with Nav */}
                        <div className="auth-header">
                            <div className="auth-brand">
                                <div className="brand-logo">
                                    <Truck size={24} />
                                </div>
                                <span className="brand-name">SpotLogs</span>
                            </div>
                            <div className="auth-nav">
                                <button 
                                    className={`nav-item ${isLogin ? 'active' : ''}`}
                                    onClick={() => setIsLogin(true)}
                                >
                                    Sign In
                                </button>
                                <button 
                                    className={`nav-item ${!isLogin ? 'active' : ''}`}
                                    onClick={() => setIsLogin(false)}
                                >
                                    Create Account
                                </button>
                            </div>
                        </div>

                        <div className="auth-welcome">
                            <h2>{isLogin ? 'Welcome Back' : 'Personalize your logs'}</h2>
                            <p>{isLogin ? 'Sign in to access your dashboard.' : 'Set up your profile to automate compliance.'}</p>
                        </div>

                        <form onSubmit={handleSubmit} className="auth-form">
                            {!isLogin && (
                                <>
                                    <div className="form-group full-width">
                                        <label htmlFor="driverName">
                                            <User size={14} /> DRIVER NAME *
                                        </label>
                                        <input
                                            type="text"
                                            id="driverName"
                                            name="driverName"
                                            value={formData.driverName}
                                            onChange={handleChange}
                                            placeholder="John Doe"
                                            required
                                            autoComplete="username"
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="vehicleUnit">
                                                <Info size={14} /> VEHICLE UNIT # *
                                            </label>
                                            <input
                                                type="text"
                                                id="vehicleUnit"
                                                name="vehicleUnit"
                                                value={formData.vehicleUnit}
                                                onChange={handleChange}
                                                placeholder="TRUCK-101"
                                                required
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="carrierName">
                                                <Building size={14} /> CARRIER NAME *
                                            </label>
                                            <input
                                                type="text"
                                                id="carrierName"
                                                name="carrierName"
                                                value={formData.carrierName}
                                                onChange={handleChange}
                                                placeholder="My Trucking Co."
                                                required
                                                autoComplete="organization"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="truckPlate">
                                                <CreditCard size={14} /> TRUCK PLATE
                                            </label>
                                            <input
                                                type="text"
                                                id="truckPlate"
                                                name="truckPlate"
                                                value={formData.truckPlate}
                                                onChange={handleChange}
                                                placeholder="ABC-1234"
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="dotNumber">
                                                <Hash size={14} /> DOT #
                                            </label>
                                            <input
                                                type="text"
                                                id="dotNumber"
                                                name="dotNumber"
                                                value={formData.dotNumber}
                                                onChange={handleChange}
                                                placeholder="1234567"
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {isLogin && (
                                <div className="form-group full-width">
                                    <label htmlFor="driverName">
                                        <User size={14} /> DRIVER NAME (LOGIN)
                                    </label>
                                    <input
                                        type="text"
                                        id="driverName"
                                        name="driverName"
                                        value={formData.driverName}
                                        onChange={handleChange}
                                        placeholder="Enter your name"
                                        required
                                        autoComplete="username"
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-group password-group">
                                    <label htmlFor="password">
                                        <Lock size={14} /> {isLogin ? 'PASSWORD' : 'SET PASSWORD *'}
                                    </label>
                                    <div className="input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder={isLogin ? "••••••••" : "Minimum 8 characters"}
                                            required
                                            autoComplete={isLogin ? "current-password" : "new-password"}
                                        />
                                        <button 
                                            type="button" 
                                            className="peek-btn"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {!isLogin && formData.password && (
                                        <div className="strength-meter" style={{ color: passwordStrength.color }}>
                                            {passwordStrength.label}
                                        </div>
                                    )}
                                </div>

                                {!isLogin && (
                                    <div className="form-group password-group">
                                        <label htmlFor="passwordConfirm">
                                            <ShieldCheck size={14} /> CONFIRM PASSWORD *
                                        </label>
                                        <div className="input-wrapper">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                id="passwordConfirm"
                                                name="passwordConfirm"
                                                value={formData.passwordConfirm}
                                                onChange={handleChange}
                                                placeholder="Re-enter password"
                                                required
                                                autoComplete="new-password"
                                            />
                                            <button 
                                                type="button" 
                                                className="peek-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        {formData.passwordConfirm && !isMatching && (
                                            <div className="match-error">Passwords don't match</div>
                                        )}
                                        {formData.passwordConfirm && isMatching && (
                                            <div className="match-success">Passwords match</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="auth-footer">
                                <Button type="submit" size="lg" icon={isLogin ? Lock : Save} className="auth-submit" disabled={isLoading}>
                                    {isLoading ? 'Verifying...' : (isLogin ? 'Sign In' : 'Save & Continue')}
                                </Button>
                                <button type="button" className="cancel-btn" onClick={onClose}>
                                    Cancel
                                </button>
                            </div>

                            <p className="privacy-note">
                                This info is saved locally on your device to ensure maximum privacy and offline access.
                            </p>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AuthModal;
